import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { DayPicker, type DateRange } from "react-day-picker";
import { format, parseISO } from "date-fns";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import "react-day-picker/style.css";

export interface DateRangePickerProps {
    dateFrom: string | null;
    dateTo: string | null;
    onDateFromChange: (value: string | null) => void;
    onDateToChange: (value: string | null) => void;
    placeholder?: string;
    className?: string;
    /** Render the calendar inline (no trigger row, always visible while
     * mounted) instead of behind a tap-to-open trigger and portal overlay. */
    inline?: boolean;
}

function isoToDate(iso: string | null): Date | undefined {
    if (!iso) return undefined;
    return parseISO(iso);
}

function dateToIso(date: Date | undefined): string | null {
    if (!date) return null;
    return format(date, "yyyy-MM-dd");
}

export function DateRangePicker({
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange,
    placeholder = "Select date range",
    className,
    inline = false,
}: DateRangePickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    // Tracks an in-progress pick (only `from` set so far). Partial picks are
    // never written upward — only complete from+to ranges flow to the parent.
    const [partialFrom, setPartialFrom] = useState<Date | null>(null);

    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.innerWidth < 768
    );

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Discard any in-progress partial when the calendar surface is dismissed
    // (portal overlay closes, or the inline picker unmounts on next render).
    // Render-time reset for the portal path keeps state in sync without an effect.
    const calendarVisible = inline || isOpen;
    if (!calendarVisible && partialFrom !== null) {
        setPartialFrom(null);
    }

    // Body scroll lock for the portal overlay only — the inline calendar
    // lives inside a scrollable drawer body that owns its own scrolling.
    useEffect(() => {
        if (isOpen && !inline) {
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [isOpen, inline]);

    // Esc dismisses the portal overlay (no-op for inline).
    useEffect(() => {
        if (!isOpen || inline) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, inline]);

    // What react-day-picker should render as the current selection.
    // Priority: an in-progress pick wins; otherwise the applied range from props.
    const selected: DateRange | undefined = partialFrom
        ? { from: partialFrom, to: undefined }
        : dateFrom
          ? { from: isoToDate(dateFrom), to: isoToDate(dateTo) }
          : undefined;

    const handleRangeSelect = (
        range: DateRange | undefined,
        selectedDay: Date
    ) => {
        // A click starts a new pick when: a complete range already exists,
        // or the user clicked a date earlier than the in-progress from.
        const startingNewPick =
            (!!dateFrom && !!dateTo && !partialFrom) ||
            (partialFrom !== null && selectedDay < partialFrom);

        if (startingNewPick) {
            setPartialFrom(selectedDay);
            // Clear any previously-applied range so the calendar shows only
            // the new partial pick; the parent's applied state is reset
            // until the user completes the second tap.
            if (dateFrom || dateTo) {
                onDateFromChange(null);
                onDateToChange(null);
            }
            return;
        }

        // Single-day collapse: react-day-picker may return { from: X, to: X }
        // when the user re-taps the same day. Treat that as restarting a pick.
        if (
            range?.from &&
            range?.to &&
            range.from.getTime() === range.to.getTime()
        ) {
            setPartialFrom(selectedDay);
            if (dateFrom || dateTo) {
                onDateFromChange(null);
                onDateToChange(null);
            }
            return;
        }

        // Completing a range: write upward, auto-apply, drop the partial.
        if (range?.from && range?.to) {
            setPartialFrom(null);
            onDateFromChange(dateToIso(range.from));
            onDateToChange(dateToIso(range.to));
            return;
        }

        // First tap of a fresh pick: remember the partial; do not apply yet.
        if (range?.from && !range?.to) {
            setPartialFrom(range.from);
        }
    };

    const formatDateRange = () => {
        const from = isoToDate(dateFrom);
        const to = isoToDate(dateTo);
        if (!from) return "";
        if (!to) return format(from, "dd/MM/yyyy");
        return `${format(from, "dd/MM/yyyy")} – ${format(to, "dd/MM/yyyy")}`;
    };

    const calendarStyleBlock = createPortal(
        <style>{`
          .date-range-picker-calendar .rdp-root {
            --rdp-accent-color: var(--primary);
            --rdp-accent-background-color: color-mix(in oklab, var(--primary) 18%, transparent);
            --rdp-range_middle-background-color: color-mix(in oklab, var(--primary) 18%, transparent);
            --rdp-range_start-color: var(--primary-foreground);
            --rdp-range_end-color: var(--primary-foreground);
            --rdp-range_start-background: var(--primary);
            --rdp-range_end-background: var(--primary);
            font-family: var(--font-sans, 'DM Sans', sans-serif);
          }
          .date-range-picker-calendar .rdp-day_button:hover:not(:disabled) {
            background-color: color-mix(in oklab, var(--primary) 25%, transparent);
            color: var(--foreground);
          }
          .date-range-picker-calendar .rdp-today:not(.rdp-selected) .rdp-day_button {
            background-color: var(--accent);
            color: var(--foreground);
            font-weight: 600;
          }
          .date-range-picker-calendar .rdp-chevron {
            fill: var(--muted-foreground);
          }
          .date-range-picker-calendar .rdp-button_previous:hover:not(:disabled) .rdp-chevron,
          .date-range-picker-calendar .rdp-button_next:hover:not(:disabled) .rdp-chevron {
            fill: var(--foreground);
          }
        `}</style>,
        document.head
    );

    if (inline) {
        return (
            <>
                <div
                    className={cn(
                        "border border-border rounded-lg bg-card p-4 date-range-picker-calendar",
                        className
                    )}
                >
                    <DayPicker
                        mode="range"
                        weekStartsOn={1}
                        selected={selected}
                        onSelect={handleRangeSelect}
                        className="m-0"
                        numberOfMonths={1}
                        fixedWeeks
                    />
                </div>
                {calendarStyleBlock}
            </>
        );
    }

    return (
        <>
            <div className={cn("relative flex flex-col", className)}>
                <div
                    className={cn(
                        "flex min-h-9 w-full items-center gap-2 rounded-md border bg-card px-3 py-1 text-sm cursor-pointer transition-colors",
                        "border-border hover:border-primary/30"
                    )}
                    onClick={() => setIsOpen(true)}
                >
                    <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span
                        className={cn(
                            "flex-1",
                            dateFrom
                                ? "text-foreground"
                                : "text-muted-foreground"
                        )}
                    >
                        {formatDateRange() || placeholder}
                    </span>
                </div>
            </div>

            {isOpen &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                        onClick={() => setIsOpen(false)}
                    >
                        <div
                            className="bg-background border border-border p-6 rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col w-full max-w-fit"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="overflow-y-auto overflow-x-hidden date-range-picker-calendar">
                                <DayPicker
                                    mode="range"
                                    weekStartsOn={1}
                                    selected={selected}
                                    onSelect={handleRangeSelect}
                                    className="m-0"
                                    numberOfMonths={isMobile ? 1 : 2}
                                    fixedWeeks
                                />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {calendarStyleBlock}
        </>
    );
}
